import { useState } from 'react';
import api from './api';
import { useToast } from './Toast';
import './WxPluginGuide.css';

const WX_PLUGIN_STEPS = [
  {
    title: '微信扫描二维码',
    description: '使用微信扫码进入公众号关注流程。',
    imageUrl: 'https://xin-feng.xyz/wx-plugin-guide/qrcode.png'
  },
  {
    title: '点击关注公众号',
    description: '进入公众号页面后，点击关注按钮。',
    imageUrl: 'https://xin-feng.xyz/wx-plugin-guide/click-follow.jpg'
  },
  {
    title: '点击弹出的验证消息',
    description: '关注后在会话中点击弹出的验证消息。',
    imageUrl: 'https://xin-feng.xyz/wx-plugin-guide/click-verify-msg.jpg'
  },
  {
    title: '点击验证身份卡片',
    description: '在验证消息中点击身份验证卡片。',
    imageUrl: 'https://xin-feng.xyz/wx-plugin-guide/click-verify-card.jpg'
  },
  {
    title: '输入手机号并申请',
    description: '随意输入符合校验规则的手机号，点击去申请。',
    imageUrl: 'https://xin-feng.xyz/wx-plugin-guide/input-phone-apply.jpg'
  },
  {
    title: '输入名字并申请加入',
    description: '随意输入符合校验规则的名字，点击申请加入。',
    imageUrl: 'https://xin-feng.xyz/wx-plugin-guide/input-name-apply.jpg'
  },
  {
    title: '等待管理员审批',
    description: '提交申请成功后，联系客服人员进行审批，等待审批通过后再点击下方按钮进入系统',
    imageUrl: 'https://xin-feng.xyz/wx-plugin-guide/apply-feedback.jpg'
  }
];

const WECHAT_APP_WARNING = '微信可直接收取上新消息提醒，无需下载企业微信 App，若误下载企业微信 App 提醒消息会自动进入企业微信 App，微信端就不再有消息提醒，需联系客服人员做额外的特殊配置，企业微信 App 不支持一键跳转代切小程序，无特殊需要请不要下载企业微信 App';

function WxPluginGuide({ onComplete, embedded = false }) {
  const [isChecking, setIsChecking] = useState(false);
  const { addToast } = useToast();

  const handleComplete = async () => {
    setIsChecking(true);
    try {
      const response = await api.get('/api/user-info');
      if (response.data.wechatReceiver?.trim()) {
        onComplete();
        return;
      }

      addToast('未检测到微信插件设置，请按步骤完成申请，然后联系客服人员进行审批，待得到审批通过的答复后再尝试进入系统', 'error');
    } catch (error) {
      addToast(error.response?.data?.error || '校验失败，请稍后重试', 'error');
    } finally {
      setIsChecking(false);
    }
  };

  const guideContent = (
    <div className="wx-plugin-guide-content">
      <section className="wx-plugin-guide-warning" role="alert" aria-label="企业微信 App 下载警告">
        <div className="wx-plugin-guide-warning-mark" aria-hidden="true">!</div>
        <div>
          <h2>重要提醒</h2>
          <p>{WECHAT_APP_WARNING}</p>
        </div>
      </section>

      <section className="wx-plugin-guide-steps" aria-label="微信插件申请步骤">
        {WX_PLUGIN_STEPS.map((step, index) => (
          <article className="wx-plugin-step-card" key={step.title}>
            <div className="wx-plugin-step-index">{String(index + 1).padStart(2, '0')}</div>
            <figure className="wx-plugin-guide-image-wrap">
              <img
                className="wx-plugin-guide-image"
                src={step.imageUrl}
                alt={`${step.title}示意图`}
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
            </figure>
            <h2>{step.title}</h2>
            <p>{step.description}</p>
          </article>
        ))}
      </section>

      {onComplete && (
        <section className="wx-plugin-guide-actions">
          <button
            className="wx-plugin-guide-complete"
            type="button"
            onClick={handleComplete}
            disabled={isChecking}
          >
            {isChecking ? '检测中...' : '我已提交申请，进入系统'}
          </button>
        </section>
      )}
    </div>
  );

  if (embedded) {
    return guideContent;
  }

  return (
    <div className="wx-plugin-guide-page">
      <div className="wx-plugin-guide-shell">
        <header className="wx-plugin-guide-header">
          <div>
            <span className="wx-plugin-guide-kicker">WeChat Plugin</span>
            <h1>微信插件申请指南</h1>
            <p>
              按下面的示意图完成公众号关注、身份验证和加入申请。申请提交后等待管理员审批即可。
            </p>
          </div>
        </header>

        {guideContent}
      </div>
    </div>
  );
}

export default WxPluginGuide;
